import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from "react";
import { docsUrl } from "@/src/features/studio-docs/docsUrl";

export type DocsLinkProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "href"
> & {
  /** Upstream langfuse.com docs path, resolved through `docsUrl`. */
  path: string;
};

/**
 * An external help link that renders nothing when Studio has no docs page
 * for `path`, so the UI never links to langfuse.com.
 */
export const DocsLink = forwardRef<HTMLAnchorElement, DocsLinkProps>(
  function DocsLink(
    { path, target = "_blank", rel = "noopener noreferrer", ...props },
    ref,
  ) {
    const href = docsUrl(path);
    if (!href) return null;
    return <a ref={ref} href={href} target={target} rel={rel} {...props} />;
  },
);

/**
 * Renders its children only when Studio has a docs page for `path`. Use it
 * when more than the link itself must disappear: a "See the docs to learn
 * more." sentence, or a button that would otherwise do nothing. A function
 * child receives the resolved URL.
 */
export function DocsGate({
  path,
  children,
}: {
  path: string;
  children: ReactNode | ((href: string) => ReactNode);
}) {
  const href = docsUrl(path);
  if (!href) return null;
  return <>{typeof children === "function" ? children(href) : children}</>;
}

/**
 * A docs link inside running text: unlinked text when Studio has no docs
 * page, so the sentence still reads.
 */
export function DocsTextLink({ children, ...props }: DocsLinkProps) {
  if (!docsUrl(props.path)) return <>{children}</>;
  return <DocsLink {...props}>{children}</DocsLink>;
}
